import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Product } from "../types/product";
import dbJson from "../data/db.json";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion"
import { ScrollToTopButton } from "./ui/scrollToTopButton";

export const ProductsDetailPage = () => {
    const [product, setProduct] = useState<Product>();

    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const selectedProduct = dbJson.productsData.find((item) => item.id === params.id);
        setProduct(selectedProduct);
    }, [params.id]);

    if (!product) return null;

    return (
        <div className="pt-16 md:pt-20 min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 py-12">
                <div className="container-custom">
                    <div className="mb-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors"
                        >
                            <ChevronLeft size={18} />
                            Назад
                        </button>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900 uppercase tracking-tight mb-4">
                        {product.name}
                    </h1>
                    <p className="text-slate-700">{product.description}</p>
                </div>
            </div>

            {/* Main content */}
            <div className="container-custom py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    {/* Left: Image + Features */}
                    <div className="space-y-6">
                        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        </div>

                        {/* Features under image */}
                        <ul className="flex flex-wrap gap-3 text-lg text-[#0056D2]">
                            {product.features.map((f, i) => (
                                <li
                                    key={i}
                                    className="bg-slate-100 px-4 py-2 rounded-md font-medium shadow-sm"
                                >
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <div>
                            <Link to="/contacts" className="btn-primary" data-testid="hero-catalog-btn">
                                Приобрести
                                <ChevronRight size={18} className="ml-2" />
                            </Link>
                        </div>


                        {product.specs && (
                            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                                <Accordion className="border-slate-100 bg-slate-50" type="multiple">
                                    <AccordionItem value="item-1">

                                        <AccordionTrigger className="px-6 py-4 text-sm font-semibold text-slate-900 uppercase tracking-wide">
                                            Характеристики
                                        </AccordionTrigger>

                                        <AccordionContent className="divide-y pl-4 pr-4 divide-slate-100 bg-white">
                                            {product.specs.map((item, i) => (
                                                <div
                                                    key={i}
                                                    className="grid grid-cols-[minmax(0,1fr)_1fr_auto] items-center gap-3 py-3"
                                                >
                                                    <span className="text-slate-800 text-base">
                                                        {item.label}
                                                    </span>

                                                    <div className="border-b border-dotted border-slate-300 translate-y-[10px] opacity-0"></div>

                                                    <span className="text-slate-900 font-medium whitespace-nowrap">
                                                        {item.value}
                                                    </span>
                                                </div>
                                            ))}
                                        </AccordionContent>
                                    </AccordionItem>
                                </Accordion>
                            </div>
                        )}
                    </div>

                    {/* Right: Details */}
                    <div className="space-y-4">
                        {product.details.map((section, index) => (
                            <div
                                key={index}
                                className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-sm transition-shadow"
                            >
                                {/* Header */}
                                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                                    <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">
                                        {section.title}
                                    </h3>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    {Array.isArray(section.content) ? (
                                        <ul className="space-y-2">
                                            {section.content.map((item, i) => (
                                                <li key={i} className="flex items-start gap-2 text-slate-700">
                                                    {/* кастомный маркер */}
                                                    <span className="w-1.5 h-1.5 mt-2 bg-[#0056D2] rounded-full flex-shrink-0"></span>
                                                    <span>{item}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-slate-700 leading-relaxed">
                                            {section.content}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <ScrollToTopButton />
            </div>
        </div>
    );
};